
import { GetRef, Table } from 'antd';
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppGlobStore } from 'src/appGlobalStore';
import { DomUtil } from 'src/util/DomUtil';
import type { GridColumnsType, GridProps, IGridEntry } from "./gridTypes";

const MIN_SCROLL_HEIGHT: number = 200;
const DEFAULT_BOTTOM_MARGIN: number = 20;
const HEIGHT_CHANGE_THRESHOLD: number = 8;

interface HeightMetrics {
  readonly viewportHeight: number;
  readonly tableTopOffset: number;
  readonly bodyTopOffset: number;
  readonly headerHeight: number;
  readonly distanceToBottom: number;
  readonly computedHeight: number;
  readonly tableBottomCoordinate: number;
  readonly expectedBottomCoordinate: number;
}

const applyBodyHeight = (tableElement: HTMLElement, height: number) => {
  const bodyElement = tableElement.querySelector('.ant-table-body');
  if (!(bodyElement instanceof HTMLElement)) return;
  bodyElement.style.maxHeight = `${height}px`;
};

const calculateViewportMetrics = (tableElement: HTMLElement): HeightMetrics => {
  const tableRect: DOMRect = tableElement.getBoundingClientRect();
  const headerElement = tableElement.querySelector('.ant-table-header');
  const headerRect: DOMRect | null = headerElement instanceof HTMLElement ? headerElement.getBoundingClientRect() : null;
  const headerHeight: number = headerRect ? headerRect.height : 0;
  const bodyTopOffset: number = headerRect ? headerRect.bottom : tableRect.top;
  const viewportHeight: number = window.innerHeight;
  const distanceToBottom: number = viewportHeight - bodyTopOffset;
  const computedHeight: number = Math.max(distanceToBottom - DEFAULT_BOTTOM_MARGIN, MIN_SCROLL_HEIGHT);
  const expectedBottomCoordinate: number = bodyTopOffset + computedHeight;
  return {
    viewportHeight,
    tableTopOffset: tableRect.top,
    bodyTopOffset,
    headerHeight,
    distanceToBottom,
    computedHeight,
    tableBottomCoordinate: tableRect.bottom,
    expectedBottomCoordinate,
  };
};

function XmVirtualTable<T extends IGridEntry>(props: Readonly<GridProps<T & IGridEntry>>) {
  const mainScrollRef = useAppGlobStore(state => state.mainScrollRef);
  const [scrollHeight, setScrollHeight] = useState<number>(() => {
    const clientHeight: number = mainScrollRef.current?.clientHeight ?? 0;
    if (props.scroll?.y && typeof props.scroll.y === 'number') return props.scroll.y;
    if (clientHeight > 0) return Math.max(clientHeight - DEFAULT_BOTTOM_MARGIN, MIN_SCROLL_HEIGHT);
    return 800;
  });
  //const [vtKey, setVtKey] = useState(0);
  //const [vt] = useVT(() => ({ scroll: { y: scrollHeight } }), [scrollHeight, vtKey]);
  const tableRef = useRef<GetRef<typeof Table>>(null);
  const postMeasureTimeoutRef = useRef<number | null>(null);
  const stabilizationFrameRef = useRef<number | null>(null);

  // Horizontal virtualization support
  const maxVisibleColumns = props.maxVisibleColumns ?? 30;
  const horizontalVirtualization = props.horizontalVirtualization === true;

  // Simplified horizontal virtualization - just limit columns if enabled
  const finalColumns = useMemo<GridColumnsType<T & IGridEntry>>(() => {
    type ColumnType = GridColumnsType<T & IGridEntry>[number];
    const columnsFromProps: ColumnType[] | undefined = props.columns ? Array.from(props.columns) : undefined;

    if (!horizontalVirtualization || !columnsFromProps) {
      return columnsFromProps;
    }

    // For now, just limit the number of columns to prevent overwhelming DOM
    // Later we can add proper scroll-based virtualization
    if (columnsFromProps.length <= maxVisibleColumns) {
      return columnsFromProps;
    }

    // Keep fixed columns (first column) + limited scrollable columns
    const partitionedColumns = columnsFromProps.reduce<{ fixed: ColumnType[]; scrollable: ColumnType[]; }>((acc, column) => {
      if ('fixed' in column && column.fixed) {
        acc.fixed.push(column);
        return acc;
      }
      acc.scrollable.push(column);
      return acc;
    }, { fixed: [], scrollable: [] });

    const availableScrollableSlots = Math.max(maxVisibleColumns - partitionedColumns.fixed.length, 0);
    const visibleScrollableColumns = partitionedColumns.scrollable.slice(0, availableScrollableSlots);

    return [...partitionedColumns.fixed, ...visibleScrollableColumns];
  }, [props.columns, horizontalVirtualization, maxVisibleColumns]);


  const updateMaxWidthOverflow = useCallback(() => {
    if (props.className?.includes('xm-table-fill')) return;

    const header: HTMLElement[] = [];
    const body: HTMLElement[] = [];
    findElementsByClass(tableRef.current?.nativeElement, "ant-table-header", header);
    findElementsByClass(tableRef.current?.nativeElement, "ant-table-body", body);
    if (header.length && mainScrollRef.current?.clientWidth)
      header[0].style.maxWidth = (mainScrollRef.current.clientWidth - 20) + "px";

    if (body.length && mainScrollRef.current?.clientWidth)
      body[0].style.maxWidth = (mainScrollRef.current.clientWidth - 20) + "px";
  }, [mainScrollRef, props.className]);

  const stopStabilization = useCallback(() => {
    if (stabilizationFrameRef.current !== null) {
      cancelAnimationFrame(stabilizationFrameRef.current);
      stabilizationFrameRef.current = null;
    }
  }, []);

  const startStabilization = useCallback((tableElement: HTMLElement, attempt: number = 0) => {
    if (attempt > 10) {
      stopStabilization();
      return;
    }

    stabilizationFrameRef.current = window.requestAnimationFrame(() => {
      const metrics: HeightMetrics = calculateViewportMetrics(tableElement);
      applyBodyHeight(tableElement, metrics.computedHeight);
      const bodyElement = tableElement.querySelector('.ant-table-body');
      const bodyRect: DOMRect | null = bodyElement instanceof HTMLElement ? bodyElement.getBoundingClientRect() : null;
      if (bodyRect) {
        const delta: number = bodyRect.height >= metrics.computedHeight
          ? Math.abs(bodyRect.height - metrics.computedHeight)
          : 0;
        if (delta <= HEIGHT_CHANGE_THRESHOLD) {
          stopStabilization();
          return;
        }
        setScrollHeight(prevHeight => {
          if (Math.abs(prevHeight - metrics.computedHeight) < HEIGHT_CHANGE_THRESHOLD) return prevHeight;
          return metrics.computedHeight;
        });
      }

      startStabilization(tableElement, attempt + 1);
    });
  }, [stopStabilization]);

  const scheduleDelayedMetrics = useCallback((tableElement: HTMLElement) => {
    if (postMeasureTimeoutRef.current !== null) window.clearTimeout(postMeasureTimeoutRef.current);
    postMeasureTimeoutRef.current = window.setTimeout(() => {
      const metrics: HeightMetrics = calculateViewportMetrics(tableElement);
      applyBodyHeight(tableElement, metrics.computedHeight);
      setScrollHeight(prevHeight => {
        if (Math.abs(prevHeight - metrics.computedHeight) < HEIGHT_CHANGE_THRESHOLD) return prevHeight;
        return metrics.computedHeight;
      });
      startStabilization(tableElement);
    }, 80);
  }, [startStabilization]);

  const recalcHeight = useCallback(() => {
    if (!tableRef.current) return;
    const tableElement: HTMLElement = tableRef.current?.nativeElement;
    const metrics: HeightMetrics = calculateViewportMetrics(tableElement);
    applyBodyHeight(tableElement, metrics.computedHeight);
    setScrollHeight(prevHeight => {
      if (Math.abs(prevHeight - metrics.computedHeight) < HEIGHT_CHANGE_THRESHOLD) return prevHeight;
      return metrics.computedHeight;
    });
    updateMaxWidthOverflow();
    scheduleDelayedMetrics(tableElement);
  }, [scheduleDelayedMetrics, updateMaxWidthOverflow]);

  const triggerInitialScroll = useCallback(() => {
    const tableBodyElement = tableRef.current?.nativeElement?.querySelector('.ant-table-body');
    if (!(tableBodyElement instanceof HTMLElement)) return;

    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

    if (isFirefox) {
      tableBodyElement.scrollTop = 0;
      requestAnimationFrame(() => {
        const scrollEvent = new Event('scroll', { bubbles: true, cancelable: false });
        tableBodyElement.dispatchEvent(scrollEvent);
        requestAnimationFrame(() => {
          tableBodyElement.scrollTop = 1;
          requestAnimationFrame(() => {
            tableBodyElement.scrollTop = 0;
            // Force virtualization refresh for Firefox
            // setTimeout(() => {
            //   setVtKey(prev => prev + 1);
            // }, 100);
          });
        });
      });
    } else {
      tableBodyElement.scrollTop = 0;
      setTimeout(() => {
        const scrollEvent = new Event('scroll', { bubbles: true });
        tableBodyElement.dispatchEvent(scrollEvent);
        tableBodyElement.scrollTop = 1;
        setTimeout(() => {
          tableBodyElement.scrollTop = 0;
        }, 100);
      }, 200);
    }
  }, []); //setVtKey


  useEffect(() => {
    const tableElement = tableRef.current;
    if (!tableElement) return undefined;
    const handleResize = () => {
      recalcHeight();
      updateMaxWidthOverflow();
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [recalcHeight, updateMaxWidthOverflow]);

  useEffect(() => {
    const tableElement = tableRef.current?.nativeElement;
    if (!tableElement) return undefined;
    const scrollableElement: HTMLElement | null = DomUtil.closestScrollable(tableElement) ?? tableElement.parentElement ?? null;
    const observerTargets: HTMLElement[] = [];
    if (scrollableElement) observerTargets.push(scrollableElement);
    observerTargets.push(tableElement);
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      recalcHeight();
    }) : null;
    if (resizeObserver) observerTargets.forEach(target => resizeObserver.observe(target));
    recalcHeight();
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [recalcHeight]);

  useEffect(() => {
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const delay = isFirefox ? 500 : 300;
    const timeoutId = setTimeout(() => {
      triggerInitialScroll();
    }, delay);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [triggerInitialScroll]);

  useEffect(() => {
    if (!tableRef.current) return;
  }, [scrollHeight]);

  useEffect(() => {
    return () => {
      if (postMeasureTimeoutRef.current !== null) window.clearTimeout(postMeasureTimeoutRef.current);
      stopStabilization();
    };
  }, [stopStabilization]);

  return (
    <Table<T>
      bordered={true}
      {...props}
      columns={finalColumns as ColumnsType<T>}
      pagination={false}
      scroll={props.noScrollY ? undefined : (props.scroll?.y ? props.scroll : { y: scrollHeight })}
      virtual
      //components={props.noScrollY ? props.components : vt}
      ref={tableRef}
    />
  );
};

function findElementsByClass(node: HTMLElement | null, className: string, foundElements: HTMLElement[]) {
  if (node?.classList?.contains(className))
    foundElements.push(node);

  if (node?.childNodes) {
    for (const childNode of node.childNodes)
      findElementsByClass(childNode as HTMLElement, className, foundElements);
  }
}

export default XmVirtualTable;